/**
 * Widget Configuration Utilities
 *
 * Utilities for resolving widget configuration from site config.
 * Handles API URL resolution and z-index configuration.
 */
import { logger } from '../core/logger';

/**
 * Resolve the effective API URL for the widget
 *
 * Priority: site config api_endpoint > passed apiUrl prop
 *
 * @param apiEndpoint API endpoint override from unified config
 * @param apiUrl API URL passed as prop
 * @param domain Current domain for logging
 * @returns Effective API URL to use
 */
export function resolveApiUrl(
    apiEndpoint: string | null | undefined,
    apiUrl: string,
    domain: string
): string {
    const effective = apiEndpoint || apiUrl;

    logger.debug(`🔍 API URL Resolution for domain: ${domain}`, {
        apiEndpoint,
        passedApiUrl: apiUrl,
        effectiveApiUrl: effective,
        override: Boolean(apiEndpoint && apiEndpoint !== apiUrl),
    });

    if (apiEndpoint && apiEndpoint !== apiUrl) {
        logger.debug(`🔄 Using config api_endpoint: ${apiEndpoint} (overriding prop: ${apiUrl})`);
    } else if (!apiEndpoint) {
        logger.debug(
            `📡 Using apiUrl prop: ${apiUrl} (no config api_endpoint for domain: ${domain})`
        );
    }

    return effective;
}

/**
 * Resolve the z-index for the widget
 *
 * @param zIndex z-index override from unified config
 * @returns z-index value (defaults to 9999 if not configured)
 */
export function resolveZIndex(zIndex: number | null | undefined): number {
    return zIndex ?? 9999;
}

/**
 * z-index applied to the shadow host while the expanded chat takes over the
 * full mobile viewport.
 *
 * In that state the close button sits at the top of the takeover, where host
 * page banners (cookie/promo) can occlude it — making Rose impossible to close
 * (IX-3375). Raising the host above page content keeps the close button
 * tappable. Collapsed bubble and desktop panel states keep the configured
 * z-index so banners can still sit on top of Rose.
 */
export const FULLSCREEN_TAKEOVER_Z_INDEX = 2147483646;
